import React from 'react';
import { motion } from 'framer-motion';
import { Shield, FileText, Clock, Mail } from 'lucide-react';

const Terms = () => {
  const sections = [
    {
      title: "1. Acceptance of Terms",
      content: `By accessing and using Aquargin's services, you accept and agree to be bound by the terms and provision of this agreement. These Terms of Service govern your use of the Aquargin platform, including all content, services, and products available at or through the website.`
    },
    {
      title: "2. Use License",
      content: `Permission is granted to temporarily download one copy of the materials on Aquargin's website for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not: modify or copy the materials; use the materials for any commercial purpose or for any public display; attempt to reverse engineer any software contained on the website; or remove any copyright or other proprietary notations from the materials.`
    },
    {
      title: "3. Service Availability",
      content: `Aquargin strives to maintain 99.9% uptime for our services. However, we do not guarantee uninterrupted or error-free service. We reserve the right to modify, suspend, or discontinue any part of our service at any time with reasonable notice to users.`
    },
    {
      title: "4. User Accounts",
      content: `To access certain features of our service, you must register for an account. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account.`
    },
    {
      title: "5. Data Security and Privacy",
      content: `Aquargin implements enterprise-grade security measures to protect your data. Your data privacy is governed by our Privacy Policy, which forms an integral part of these terms. We do not sell, trade, or otherwise transfer your personal data to third parties without your consent.`
    },
    {
      title: "6. Intellectual Property",
      content: `The service and its original content, features, and functionality are and will remain the exclusive property of Aquargin and its licensors. The service is protected by copyright, trademark, and other laws. Our trademarks and trade dress may not be used without our prior written consent.`
    },
    {
      title: "7. User Content",
      content: `You retain all rights to any content you submit, post, or display on or through the service. By submitting content, you grant us a worldwide, non-exclusive, royalty-free license to use, copy, reproduce, process, adapt, modify, publish, transmit, display, and distribute such content in any media.`
    },
    {
      title: "8. Prohibited Uses",
      content: `You may not use our service: for any unlawful purpose or to solicit others to engage in unlawful acts; to violate any international, federal, provincial, or state regulations, rules, laws, or local ordinances; to infringe upon or violate our intellectual property rights or the intellectual property rights of others; to harass, abuse, insult, harm, defame, slander, disparage, intimidate, or discriminate; to submit false or misleading information.`
    },
    {
      title: "9. Subscription and Billing",
      content: `Our services are offered on a subscription basis. Billing cycles, pricing, and payment terms are as specified in your subscription agreement. All fees are non-refundable except as required by law or as specifically stated in our refund policy. We reserve the right to change our pricing with 30 days' notice.`
    },
    {
      title: "10. Limitation of Liability",
      content: `In no event shall Aquargin, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your use of the service.`
    },
    {
      title: "11. Termination",
      content: `We may terminate or suspend your account and bar access to the service immediately, without prior notice or liability, under our sole discretion, for any reason whatsoever and without limitation, including but not limited to a breach of the Terms. Upon termination, your right to use the service will cease immediately.`
    },
    {
      title: "12. Changes to Terms",
      content: `We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.`
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary/10 via-white to-secondary/5 py-20 lg:py-32">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5 opacity-40"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="flex items-center justify-center mb-6"
            >
              <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-full p-4 mr-4">
                <FileText className="h-8 w-8 text-primary" />
              </div>
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900">
                Terms & Conditions
              </h1>
            </motion.div>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl text-gray-600 mb-8"
            >
              Please read these terms and conditions carefully before using our service.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex items-center justify-center space-x-6 text-gray-600"
            >
              <div className="flex items-center">
                <Clock className="h-5 w-5 mr-2" />
                <span>Last updated: January 1, 2025</span>
              </div>
              <div className="flex items-center">
                <Shield className="h-5 w-5 mr-2" />
                <span>Effective immediately</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-xl p-8 mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Welcome to Aquargin</h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                These Terms of Service ("Terms") govern your use of the Aquargin platform and services. 
                By accessing or using our service, you agree to be bound by these Terms. If you disagree 
                with any part of these terms, then you may not access the service.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Terms Sections */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {sections.map((section, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl p-8 shadow-lg"
                >
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    {section.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {section.content}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-xl p-8">
              <Mail className="h-12 w-12 text-primary mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Questions About These Terms?
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                If you have any questions about these Terms of Service, please contact our legal team.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="mailto:legal@aquargin.com"
                  className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-8 py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300"
                >
                  Contact Legal Team
                </a>
                <a
                  href="/contact"
                  className="border-2 border-primary text-primary px-8 py-3 rounded-lg font-semibold hover:bg-primary hover:text-white transition-all duration-300"
                >
                  General Contact
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Terms; 